// lib/demoDataService.ts
import { supabase } from './supabaseClient';
import { DEMO_PROBLEM_BANK_DATA } from '@/data/demoProblemBankData';

// Storage keys with user prefix for account isolation
const getStorageKey = (userId: string, key: string) => `demo_${userId}_${key}`;

// Demo accounts list - ONLY demo accounts
export const DEMO_ACCOUNTS = [
  'demo.senior@vett.com',
  'demo.problembank@vett.com',
  'demo.business@vett.com',
  'demo@founder.com',
  // Real accounts removed - they should NOT have demo features
];

export const demoDataService = {
  /**
   * Check if current user is a demo account
   */
  isDemoAccount: (email?: string | null): boolean => {
    if (!email) return false;
    return DEMO_ACCOUNTS.includes(email.toLowerCase());
  },

  /**
   * Get a consistent user ID for storage (handles fake IDs)
   */
  getStorageUserId: (userId?: string, email?: string): string => {
    // If we have a real UUID, use it
    if (userId && userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return userId;
    }
    // If it's a fake demo ID, use email as fallback
    if (email) {
      return email.replace(/[^a-zA-Z0-9]/g, '_');
    }
    // Last resort
    return userId || 'demo_user';
  },

  /**
   * Load demo data for a specific user (account-specific storage)
   */
  loadDemoData: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    
    if (!storageId) {
      console.error('No valid identifier for demo data load');
      return { success: false, error: 'No user identifier provided' };
    }

    try {
      console.log(`📊 Loading demo data for user: ${storageId}`);
      
      // Store data with user-specific keys - isolates demo data per account
      localStorage.setItem(getStorageKey(storageId, 'problems'), JSON.stringify(DEMO_PROBLEM_BANK_DATA.analystProblems));
      localStorage.setItem(getStorageKey(storageId, 'submissions'), JSON.stringify(DEMO_PROBLEM_BANK_DATA.externalSubmissions));
      localStorage.setItem(getStorageKey(storageId, 'accepted'), JSON.stringify(DEMO_PROBLEM_BANK_DATA.acceptedSubmissions || []));
      localStorage.setItem(getStorageKey(storageId, 'interest'), JSON.stringify(DEMO_PROBLEM_BANK_DATA.squadInterest || []));
      localStorage.setItem(getStorageKey(storageId, 'squads'), JSON.stringify(DEMO_PROBLEM_BANK_DATA.squads || []));
      localStorage.setItem(getStorageKey(storageId, 'active'), 'true');
      localStorage.setItem(getStorageKey(storageId, 'loaded'), 'true');
      
      console.log(`✅ Demo data loaded for user: ${storageId}`);
      
      return { 
        success: true, 
        data: {
          totalAnalystProblems: DEMO_PROBLEM_BANK_DATA.analystProblems.length,
          totalExternalSubmissions: DEMO_PROBLEM_BANK_DATA.externalSubmissions.length,
          summary: DEMO_PROBLEM_BANK_DATA.summary
        } 
      };
    } catch (error) {
      console.error('❌ Failed to load demo data:', error);
      return { success: false, error };
    }
  },

  /**
   * Clear demo data for a specific user
   */
  clearDemoData: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    
    if (!storageId) {
      console.error('No valid identifier for clearing demo data');
      return { success: false, error: 'No user identifier provided' };
    }

    try {
      localStorage.removeItem(getStorageKey(storageId, 'problems'));
      localStorage.removeItem(getStorageKey(storageId, 'submissions'));
      localStorage.removeItem(getStorageKey(storageId, 'accepted'));
      localStorage.removeItem(getStorageKey(storageId, 'interest'));
      localStorage.removeItem(getStorageKey(storageId, 'squads'));
      localStorage.removeItem(getStorageKey(storageId, 'active'));
      localStorage.removeItem(getStorageKey(storageId, 'loaded'));
      
      console.log(`✅ Demo data cleared for user: ${storageId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to clear demo data:', error);
      return { success: false, error };
    }
  },

  /**
   * Check if demo mode is active for a specific user
   */
  isDemoModeActive: (userId?: string, userEmail?: string): boolean => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return false;
    return localStorage.getItem(getStorageKey(storageId, 'active')) === 'true';
  },

  /**
   * Check if demo data is loaded for a specific user
   */
  isDemoDataLoaded: (userId?: string, userEmail?: string): boolean => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return false;
    return localStorage.getItem(getStorageKey(storageId, 'loaded')) === 'true';
  },

  /**
   * Get demo problems for a specific user
   */
  getDemoProblems: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return [];
    const stored = localStorage.getItem(getStorageKey(storageId, 'problems'));
    return stored ? JSON.parse(stored) : [];
  },

  /**
   * Get demo submissions for a specific user
   */
  getDemoSubmissions: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return [];
    const stored = localStorage.getItem(getStorageKey(storageId, 'submissions'));
    return stored ? JSON.parse(stored) : [];
  },

  /**
   * Get accepted submissions for a specific user
   */
  getAcceptedSubmissions: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return [];
    const stored = localStorage.getItem(getStorageKey(storageId, 'accepted'));
    return stored ? JSON.parse(stored) : [];
  },

  /**
   * Get demo squads for a specific user
   */
  getDemoSquads: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return [];
    const stored = localStorage.getItem(getStorageKey(storageId, 'squads'));
    return stored ? JSON.parse(stored) : [];
  },

  /**
   * Get squad interest for a specific user
   */
  getSquadInterest: (userId?: string, userEmail?: string) => {
    const storageId = demoDataService.getStorageUserId(userId, userEmail);
    if (!storageId) return [];
    const stored = localStorage.getItem(getStorageKey(storageId, 'interest'));
    return stored ? JSON.parse(stored) : [];
  },

  /**
   * Get demo analysts
   */
  getDemoAnalysts: () => {
    return DEMO_PROBLEM_BANK_DATA.analysts || [];
  },

  /**
   * Get summary stats
   */
  getSummary: () => {
    return DEMO_PROBLEM_BANK_DATA.summary;
  },

  /**
   * Seed the database with demo data (for development)
   */
  seedDatabase: async (userId?: string, userEmail?: string) => {
    return demoDataService.loadDemoData(userId, userEmail);
  }
};